<script setup>
  import {inject, nextTick, onMounted, ref} from "vue";

  import ComponentDialog from "@components/Dialogs/Dialog.vue";
  import ComponentUniversalPatientAssignment from "@components/PatientSelect/UniversalPatientAssignment.vue";
  import {useForm} from "@inertiajs/vue3";
  import {MedButton, MedSpinner} from "@pharma4u/medicheck-components";

  defineOptions({
    name: "ComponentTransferImportDialog",
  });

  const props = defineProps({
    transfer: {
      type: Object,
      required: true,
    },
  });

  const privacy = inject("$privacy");

  const refDialog = ref(null);
  const refUniversalPatientAssignment = ref(null);

  const form = useForm({
    patient: null,
  });

  const decryptedPatient = ref(null);

  onMounted(() => {
    refDialog.value.open();
    privacy.decryptPatient(props.transfer.data.patient).then((decryptedObject) => {
      decryptedPatient.value = decryptedObject;

      // if the given date is invalid (invalid format or whatever)
      // we set the value to null
      if (Number.isNaN(new Date(decryptedPatient.value?.birthdate).getTime())) {
        decryptedPatient.value.birthdate = null;
        decryptedPatient.value.birthdate_formatted = null;
      }

      // replace an empty string insurancenumber with null (this field is not required)
      if (decryptedPatient.value?.insurancenumber === "") {
        decryptedPatient.value.insurancenumber = null;
      }

      nextTick(() => {
        refUniversalPatientAssignment.value.setPatient(decryptedPatient.value);
      });
    });
  });

  function close() {
    refDialog.value.close();
    refUniversalPatientAssignment.value.clearPatient();
    decryptedPatient.value = null;
  }

  const save = (patient, isNewPatient = false) => {
    privacy.encryptPatient(patient).then((encryptedPatient) => {
      form
        .transform(() => {
          // we only have to send the re-encrypted patient (with clipped values),
          // all the transfer data already exists in data service
          return {patient: encryptedPatient};
        })
        .post(route("init-from-transfer", {transfer: props.transfer.id, patient: isNewPatient ? null : patient?.id}), {
          preserveScroll: true,
          onError: (error) => {
            console.error(error);
          },
        });
    });
  };

  const addToExistingPatient = (selectedPatient) => {
    save(selectedPatient, false);
  };

  const newPatient = (patient) => {
    save(patient, true);
  };
</script>

<template>
  <ComponentDialog
    ref="refDialog"
    dialog-title="Import aus Warenwirtschaft"
    dialog-width="large"
    :test-id="getTestId('transfer-import')"
  >
    <template #content>
      <div>
        <ComponentUniversalPatientAssignment
          v-if="decryptedPatient"
          ref="refUniversalPatientAssignment"
          :test-id="getTestId('transfer-import-dialog-universal-patient-assignment')"
          @existing-patient-selected="(patient) => addToExistingPatient(patient)"
          @new-patient="(patient) => newPatient(patient)"
        />

        <div v-else class="flex flex-col items-center justify-center py-10">
          <MedSpinner size="2xl" :data-test="getTestId('transfer-import-dialog-spinner')" />
        </div>
      </div>
    </template>

    <template #actions>
      <!-- <Teleport> target of UniversalPatientAssignment component (reverts space-x margin when empty) -->
      <div id="UniversalPatientAssignmentSearchButton" class="empty:-ml-3" />

      <MedButton :data-test="getTestId('transfer-import-dialog-close-button')" @click="close()">Abbrechen</MedButton>
    </template>
  </ComponentDialog>
</template>
